import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryKeys } from './keys.js';
import { useInventoryClient } from './provider.js';
import { useIdempotentMutation } from './useIdempotentMutation.js';
function invalidateHolds(queryClient) {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.reservations.all() });
    // A hold changes `issuable` and `available_to_promise` without changing `on_hand`, so
    // the availability queries must go too.
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.stock.all() });
}
// --------------------------------------------------------------- availability
/**
 * The four numbers.
 *
 * `on_hand`, `reserved_hard`, `reserved_soft`, `issuable`, `available_to_promise` — and a
 * screen must show them as distinct figures. `issuable` is what a goods issue will be
 * allowed to take; `available_to_promise` is what a salesperson may commit. Rendering one
 * of them under the label "available" is the mistake this endpoint exists to prevent.
 *
 * Cheap to poll: the server computes it without taking a lock and without writing.
 */
export function useAvailability(params, options) {
    const client = useInventoryClient();
    const ready = Boolean(params.item_id && params.warehouse_id);
    const resolved = {
        item_id: params.item_id ?? '',
        warehouse_id: params.warehouse_id ?? '',
        ...(params.stock_state ? { stock_state: params.stock_state } : {}),
    };
    return useQuery({
        queryKey: inventoryKeys.stock.availability(resolved),
        queryFn: ({ signal }) => client.reservations.availability(resolved, signal),
        enabled: ready,
        ...options,
    });
}
// --------------------------------------------------------------- reservations
export function useReservations(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.reservations.list(filter),
        queryFn: ({ signal }) => client.reservations.list(filter, signal),
        ...options,
    });
}
export function useReservation(reservationId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.reservations.detail(reservationId ?? ''),
        queryFn: ({ signal }) => client.reservations.get(reservationId, signal),
        enabled: Boolean(reservationId),
        ...options,
    });
}
export function useCreateReservation() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.reservations.create(input, operation),
        onSuccess: () => invalidateHolds(queryClient),
    });
}
export function useReleaseReservation(reservationId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.reservations.release(reservationId, input, operation),
        onSuccess: () => invalidateHolds(queryClient),
    });
}
export function useReleaseExpiredReservations() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.reservations.releaseExpired(input.warehouse_id, operation),
        onSuccess: () => invalidateHolds(queryClient),
    });
}
// -------------------------------------------------------------------- counting
export function useCountSessions(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.counting.list(filter),
        queryFn: ({ signal }) => client.counting.list(filter, signal),
        ...options,
    });
}
export function useCountSession(sessionId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.counting.detail(sessionId ?? ''),
        queryFn: ({ signal }) => client.counting.get(sessionId, signal),
        enabled: Boolean(sessionId),
        ...options,
    });
}
export function useCountSummary(sessionId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.counting.summary(sessionId ?? ''),
        queryFn: ({ signal }) => client.counting.summary(sessionId, signal),
        enabled: Boolean(sessionId),
        ...options,
    });
}
/**
 * Recompute the signed manifest.
 *
 * `valid: false` means a counted line was edited after signing. Show it as an alarm, not
 * as a field on a form.
 */
export function useVerifyCountManifest(sessionId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.counting.manifest(sessionId ?? ''),
        queryFn: ({ signal }) => client.counting.verifyManifest(sessionId, signal),
        enabled: Boolean(sessionId),
        ...options,
    });
}
export function useCreateCountSession() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.counting.create(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.counting.all() });
        },
    });
}
export function useFreezeCountSession(sessionId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (_input, operation) => client.counting.freeze(sessionId, operation),
        onSuccess: (session) => {
            queryClient.setQueryData(inventoryKeys.counting.detail(sessionId), session);
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.counting.all() });
        },
    });
}
export function useRecordCount(sessionId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.counting.recordEntries(sessionId, input, operation),
        onSuccess: (session) => {
            queryClient.setQueryData(inventoryKeys.counting.detail(sessionId), session);
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.counting.all() });
        },
    });
}
/** Sealing the count raises the approval. Approval posts a delta adjustment (D-32). */
export function useSubmitCountSession(sessionId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.counting.submit(sessionId, input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.counting.all() });
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.stock.all() });
        },
    });
}
export function useCancelCountSession(sessionId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (_input, operation) => client.counting.cancel(sessionId, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.counting.all() });
        },
    });
}
// ------------------------------------------------------ reorder & replenishment
export function useReorderRules(warehouseId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.replenishment.rules(warehouseId),
        queryFn: ({ signal }) => client.replenishment.listRules(warehouseId, signal),
        ...options,
    });
}
export function useReorderRule(params, options) {
    const client = useInventoryClient();
    const resolved = { item_id: params.item_id ?? '', warehouse_id: params.warehouse_id ?? '' };
    return useQuery({
        queryKey: inventoryKeys.replenishment.rule(resolved),
        queryFn: ({ signal }) => client.replenishment.getRule(resolved, signal),
        enabled: Boolean(params.item_id && params.warehouse_id),
        retry: false,
        ...options,
    });
}
export function useUpsertReorderRule() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.replenishment.upsertRule(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.replenishment.all() });
        },
    });
}
export function useDeleteReorderRule() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.replenishment.deleteRule(input.rule_id, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.replenishment.all() });
        },
    });
}
export function useReplenishmentRuns(warehouseId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.replenishment.runs(warehouseId),
        queryFn: ({ signal }) => client.replenishment.listRuns(warehouseId, signal),
        ...options,
    });
}
export function useReplenishmentRun(runId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.replenishment.run(runId ?? ''),
        queryFn: ({ signal }) => client.replenishment.getRun(runId, signal),
        enabled: Boolean(runId),
        ...options,
    });
}
export function useSuggestions(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.replenishment.suggestions(filter),
        queryFn: ({ signal }) => client.replenishment.listSuggestions(filter, signal),
        ...options,
    });
}
/** Running replenishment produces suggestions and orders nothing (D-34). */
export function useRunReplenishment() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.replenishment.run(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.replenishment.all() });
        },
    });
}
export function useConvertSuggestions() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.replenishment.convertSuggestions(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.replenishment.all() });
        },
    });
}
export function useDismissSuggestion() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.replenishment.dismissSuggestion(input.suggestion_id, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.replenishment.all() });
        },
    });
}
//# sourceMappingURL=planning.js.map